import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Linkedin, Instagram, Twitter, Globe, Mail, Phone, Trash2, Plus, MessageSquare, Calendar, Send } from "lucide-react";
import { adminApi, socialMessageHref } from "@/lib/adminApi";

const ACTION_KINDS = [
  { k: "note", Icon: MessageSquare, label: "Note" },
  { k: "call", Icon: Phone, label: "Call" },
  { k: "email", Icon: Mail, label: "Email" },
  { k: "meeting", Icon: Calendar, label: "Meeting" },
  { k: "linkedin", Icon: Linkedin, label: "LinkedIn DM" },
  { k: "instagram", Icon: Instagram, label: "Instagram DM" },
];

const ContactDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [contact, setContact] = useState(null);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAction, setShowAction] = useState(false);
  const [editForm, setEditForm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { contact: c, actions: a } = await adminApi.getContact(id);
      setContact(c);
      setActions(a || []);
      setEditForm({
        name: c.name || "", email: c.email || "", company: c.company || "", phone: c.phone || "",
        position: c.position || "", country: c.country || "", status: c.status || "lead",
        notes: c.notes || "", tags: (c.tags || []).join(", "),
        linkedin: c.socials?.linkedin || "", instagram: c.socials?.instagram || "",
        twitter: c.socials?.twitter || "", website: c.socials?.website || "",
      });
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const save = async () => {
    const payload = {
      name: editForm.name, email: editForm.email || null, company: editForm.company,
      phone: editForm.phone, position: editForm.position, country: editForm.country,
      status: editForm.status, notes: editForm.notes,
      tags: editForm.tags.split(",").map((t) => t.trim()).filter(Boolean),
      socials: {
        linkedin: editForm.linkedin, instagram: editForm.instagram,
        twitter: editForm.twitter, website: editForm.website,
      },
    };
    const c = await adminApi.updateContact(id, payload);
    setContact(c);
  };

  const removeContact = async () => {
    if (!window.confirm("Delete this contact and all its actions?")) return;
    await adminApi.deleteContact(id);
    navigate("/admin/crm");
  };

  const removeAction = async (aid) => {
    await adminApi.deleteAction(aid);
    setActions(actions.filter((a) => a.id !== aid));
  };

  if (loading || !contact || !editForm) {
    return <div className="text-sm text-[color:var(--gv-ink)]/50">Loading…</div>;
  }

  return (
    <div data-testid="admin-contact-detail">
      <Link to="/admin/crm" className="inline-flex items-center gap-1 text-sm text-[color:var(--gv-ink)]/60 hover:text-[color:var(--gv-pink)] mb-4">
        <ArrowLeft size={14} /> Back to list
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl text-[color:var(--gv-ink)]">{contact.name}</h1>
          <p className="text-sm text-[color:var(--gv-ink)]/60">{contact.company || "—"}</p>
        </div>
        <div className="flex items-center gap-2">
          {contact.email && (
            <a href={`mailto:${contact.email}`} className="btn-secondary" data-testid="contact-email-btn">
              <Mail size={14} /> Email
            </a>
          )}
          {contact.socials?.linkedin && (
            <a href={socialMessageHref(contact, "linkedin")} target="_blank" rel="noreferrer" className="btn-secondary" data-testid="contact-linkedin-btn">
              <Linkedin size={14} /> Message LinkedIn
            </a>
          )}
          {contact.socials?.instagram && (
            <a href={contact.socials.instagram} target="_blank" rel="noreferrer" className="btn-secondary" data-testid="contact-instagram-btn">
              <Instagram size={14} /> Instagram
            </a>
          )}
          <button onClick={removeContact} className="px-3 py-2 rounded-[3px] text-sm text-rose-600 hover:bg-rose-50" data-testid="contact-delete-btn">
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: edit form */}
        <div className="lg:col-span-2 space-y-5">
          <Section title="Profile">
            <Grid>
              <FormInput label="Name" k="name" form={editForm} setForm={setEditForm} />
              <FormInput label="Email" k="email" type="email" form={editForm} setForm={setEditForm} />
              <FormInput label="Company" k="company" form={editForm} setForm={setEditForm} />
              <FormInput label="Phone" k="phone" form={editForm} setForm={setEditForm} />
              <FormInput label="Position" k="position" form={editForm} setForm={setEditForm} />
              <FormInput label="Country" k="country" form={editForm} setForm={setEditForm} />
              <FormSelect label="Status" k="status" form={editForm} setForm={setEditForm} options={["lead", "contacted", "qualified", "customer", "lost"]} />
              <FormInput label="Tags (comma-separated)" k="tags" form={editForm} setForm={setEditForm} />
            </Grid>
          </Section>
          <Section title="Socials">
            <Grid>
              <FormInput label="LinkedIn URL" k="linkedin" form={editForm} setForm={setEditForm} />
              <FormInput label="Instagram URL" k="instagram" form={editForm} setForm={setEditForm} />
              <FormInput label="Twitter/X URL" k="twitter" form={editForm} setForm={setEditForm} />
              <FormInput label="Website" k="website" form={editForm} setForm={setEditForm} />
            </Grid>
          </Section>
          <Section title="Notes">
            <textarea
              value={editForm.notes}
              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
              rows={5}
              className="w-full border border-[color:var(--gv-line)] rounded-[3px] px-3 py-2 text-sm"
              data-testid="contact-notes"
            />
          </Section>
          <button onClick={save} className="btn-primary" data-testid="contact-save-btn">
            <Send size={14} /> Save changes
          </button>
        </div>

        {/* Right: activity timeline */}
        <aside className="space-y-4">
          <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg text-[color:var(--gv-ink)]">Activity</h3>
              <button onClick={() => setShowAction(true)} className="text-[color:var(--gv-pink)] text-sm flex items-center gap-1 hover:underline" data-testid="action-add-btn">
                <Plus size={14} /> Log action
              </button>
            </div>
            {actions.length === 0 ? (
              <div className="text-sm text-[color:var(--gv-ink)]/50">No activity yet.</div>
            ) : (
              <ul className="space-y-3" data-testid="action-list">
                {actions.map((a) => <ActionItem key={a.id} action={a} onDelete={() => removeAction(a.id)} />)}
              </ul>
            )}
          </div>
        </aside>
      </div>

      {showAction && <ActionModal contactId={id} onClose={() => setShowAction(false)} onCreated={(a) => { setActions([a, ...actions]); setShowAction(false); }} />}
    </div>
  );
};

const Section = ({ title, children }) => (
  <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5">
    <h3 className="font-display text-lg text-[color:var(--gv-ink)] mb-4">{title}</h3>
    {children}
  </div>
);

const Grid = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;

const FormInput = ({ label, k, form, setForm, type = "text" }) => (
  <label className="block">
    <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/60 mb-1">{label}</div>
    <input type={type} value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full border border-[color:var(--gv-line)] rounded-[3px] px-3 py-2 text-sm" data-testid={`field-${k}`} />
  </label>
);

const FormSelect = ({ label, k, form, setForm, options }) => (
  <label className="block">
    <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/60 mb-1">{label}</div>
    <select value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="w-full border border-[color:var(--gv-line)] rounded-[3px] px-3 py-2 text-sm" data-testid={`field-${k}`}>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </label>
);

const ActionItem = ({ action, onDelete }) => {
  const meta = ACTION_KINDS.find((k) => k.k === action.kind) || ACTION_KINDS[0];
  const Icon = meta.Icon;
  return (
    <li className="flex items-start gap-3 group" data-testid={`action-${action.id}`}>
      <div className="w-8 h-8 rounded-full bg-[color:var(--gv-mist)] flex items-center justify-center text-[color:var(--gv-blue)] shrink-0">
        <Icon size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="text-[13px] font-medium text-[color:var(--gv-ink)]">{action.title || meta.label}</div>
          <button onClick={onDelete} className="text-[color:var(--gv-ink)]/30 hover:text-rose-500 opacity-0 group-hover:opacity-100"><Trash2 size={12} /></button>
        </div>
        {action.body && <div className="text-[12.5px] text-[color:var(--gv-ink)]/70 leading-relaxed mt-0.5 whitespace-pre-wrap">{action.body}</div>}
        <div className="text-[11px] text-[color:var(--gv-ink)]/50 mt-1">{formatDate(action.at)}</div>
      </div>
    </li>
  );
};

function formatDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

const ActionModal = ({ contactId, onClose, onCreated }) => {
  const [kind, setKind] = useState("note");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const a = await adminApi.addAction(contactId, { kind, title, body });
      onCreated(a);
    } finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="modal-add-action">
      <div className="bg-white rounded-[5px] max-w-md w-full" onClick={(e) => e.stopPropagation()}>
        <div className="px-6 py-4 border-b border-[color:var(--gv-line)]">
          <h3 className="font-display text-xl text-[color:var(--gv-ink)]">Log an action</h3>
        </div>
        <form onSubmit={submit} className="p-6">
          <label className="block mb-3">
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/60 mb-1">Type</div>
            <select value={kind} onChange={(e) => setKind(e.target.value)} className="w-full border border-[color:var(--gv-line)] rounded-[3px] px-3 py-2 text-sm" data-testid="action-kind">
              {ACTION_KINDS.map((k) => <option key={k.k} value={k.k}>{k.label}</option>)}
            </select>
          </label>
          <label className="block mb-3">
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/60 mb-1">Title</div>
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Demo call follow-up" className="w-full border border-[color:var(--gv-line)] rounded-[3px] px-3 py-2 text-sm" data-testid="action-title" />
          </label>
          <label className="block mb-4">
            <div className="text-[11px] uppercase tracking-widest text-[color:var(--gv-ink)]/60 mb-1">Notes</div>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4} className="w-full border border-[color:var(--gv-line)] rounded-[3px] px-3 py-2 text-sm" data-testid="action-body" />
          </label>
          <button disabled={loading} className="btn-primary w-full justify-center disabled:opacity-50" data-testid="action-submit">
            {loading ? "Saving…" : "Save action"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ContactDetailPage;
